<template>
  <div class="project-list-container">
    <v-card
      class="projects-list"
      height="100%"
    >
      <div class="sticky-header">
        <v-card-title class="pb-0 projects-title">
          Projects
          <v-spacer></v-spacer>
          <div class="projects-title-actions">
            <div class="mr-3">
              <v-text-field
                v-model="search"
                label="Search"
                dense
                hide-details
                clearable
                class="ml-1"
                clear-icon="mdi-close-circle-outline"
                @input="updateFilteredProjectsList()"
              ></v-text-field>
              <v-checkbox
                v-model="filterClosedFoldersTasks"
                dense
                hide-details
                label="Show 'Completed' Folders/Tasks"
                color="primary"
                class="mb-1 normal-worlds-wrapping"
              ></v-checkbox>
            </div>
            <v-btn
              icon
              color="primary"
              @click="showSettingsList = true"
            >
              <v-icon>mdi-cog</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-divider class="divider"></v-divider>
      </div>
      <v-card-text class="card-text-container">
        <template v-if="loading">
          <div class="loading-container">
            Loading...
          </div>
        </template>
        <div v-if="!loading">
          <v-checkbox
            dense
            hide-details
            class="pa-0 ma-0 mb-2 pl-3"
            label="Select All"
            :indeterminate="indeterminate"
            @change="onSelectAllChanged"
          ></v-checkbox>
        </div>
        <div :key="'select_all_' + selectAllProjects">
          <v-treeview
            ref="projectlistTree"
            :items="projectsTree"
            :search="search"
            :open.sync="openedFolders"
            hoverable
            item-text="Name"
            selectable
            selected-color="primary"
            dense
            item-children="Tasks"
            item-key="Item_ID"
            return-object
            v-model="selects"
            @input="onSelect"
          >
            <template v-slot:prepend="{ item, open }">
              <template v-if="['Project', 'Folder'].indexOf(item.Type) > -1">
                <div class="mr-2" @click="onLabelClick(item, open)">
                  <v-icon color="primary" v-if="['integrated', 'integrated-employee'].indexOf(item.Jira_Task_Key) > -1">
                    mdi-atlassian mdi-18px
                  </v-icon>
                  <v-icon
                    :color="item.Status === 'Completed' && item.Type === 'Folder' ? 'blue-grey lighten-3' : 'primary'">
                    {{ open && item.Tasks.length ? 'mdi-folder-open' : 'mdi-folder' }}
                  </v-icon>
                </div>
              </template>
              <v-icon class="mr-2" :color="item.Status === 'Completed' ? 'blue-grey lighten-3' : 'success'" v-else>
                mdi-file-code
              </v-icon>
            </template>
            <template v-slot:label="{ item, open }">
              <div
                @click="onLabelClick(item, open)"
              >
                <template
                  v-if="['Project'].indexOf(item.Type) > -1"
                >
                  {{ item.Name }}
                </template>
                <template
                  v-else
                >
                  <div class="d-flex align-center">
                    <div style="max-width: calc(100% - 110px); white-space: normal;">
                      {{ item.Name }} (Est.:{{item.Estimate}}/Rep.:{{item.Reported}})
                      <small class="d-block">{{ item.Full_Name }}</small>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn
                      x-small
                      color="success"
                      v-if="item.Type === 'Task'"
                      @click="addReporting(item)"
                    >add reporting</v-btn>
                  </div>
                </template>
              </div>
            </template>
            <template v-slot:append="{ item }">
              <ConfigDropdown
                :itemData="item"
                @onPlusClick="addToOpened"
                @onImportFromJiraClick="onImportFromJiraClick"
                @onRefresh="updateProjectTasksList"
                @moveTo="moveTo"
              />
            </template>
          </v-treeview>
        </div>
      </v-card-text>
    </v-card>
    <ProjectsListSettingsDialog
      v-model="showSettingsList"
      @updateProjectsList="getProjectsListWithTasks"
    />
    <CRUDTaskOrFolderDialog
      v-if="showCRUDTaskOrFolderDialog"
      @updateTasks="updateProjectTasksList"
    />
    <AlertAboutDeleteDialog
      @updateTasks="updateProjectTasksList"
    />
    <v-dialog
      v-model="chooseDateRangeToImportFromJira"
      max-width="500"
      dense
    >
      <v-card>
        <v-card-title>
          Choose dates for import
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          <RangeDatePickerMenu
            v-model="importFromJiraDateRange"
            label="Period"
            showButtons
          />
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn small outlined color="primary" @click="chooseDateRangeToImportFromJira = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn small color="primary" @click="importReportsFromJira">Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Information dialog -->
    <v-dialog
      v-model="waitForNotification"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Importing JIRA reports in progress
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          Please wait for a notification that the import is complete.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small color="primary" @click="waitForNotification = false">Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- /Information dialog -->
    <!-- Jira API Token dialog -->
    <v-dialog
      v-model="jiraApiTokenDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Jira Access
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          Please fill all the fields to get the access to the Jira REST API.
          To get API Token go to the
          <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" class="px-1">API Tokens manager</a>
          and create new one. After that paste your token to the "Jira API Token" field.
          <v-text-field
            class="mt-5 pt-3"
            dense
            hide-details
            label="Jira login"
            v-model="jiraApiDefaultLogin"
          ></v-text-field>
          <v-text-field
            class="mt-5 pt-3"
            dense
            hide-details
            label="Jira API Token"
            type="password"
            v-model="jiraApiToken"
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn small outlined color="primary" @click="jiraApiTokenDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            :disabled="!jiraApiDefaultLogin || !jiraApiToken"
            @click="saveJiraRestApiAccessInformation">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- /Jira API Token dialog -->
    <!-- Add new Project to current List dialog -->
    <v-dialog
      v-model="showRedirectedDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Selected project is not on the list
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          Selected project will added to your list. Do you want to add it?
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn small color="red darken-2 white--text" @click="showRedirectedDialog = false">Proceed without adding</v-btn>
          <v-btn small color="success" @click="addRedirectedProjectToList">Yes, add project</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- /Add new Project to current List dialog -->
    <!-- Add new Projects by Client to current List dialog -->
    <v-dialog
      v-model="showRedirectedClientDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Selected {{ this.notIncludedProjects.length > 1 ? 'projects are' : 'project is' }} not on the list
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          {{ this.notIncludedProjects.length }} {{ this.notIncludedProjects.length > 1 ? 'projects will be added to the grid. Do you want to add them?' : 'project will be added to the grid. Do you want to add it?'}}
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn small color="red darken-2 white--text" @click="selectRedirectedProjectsWithoutAdding">Proceed without adding</v-btn>
          <v-btn small color="success" @click="addRedirectedProjectsByClientToList">Yes, add project(s)</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- /Add new Projects by Client to current List dialog -->
    <!-- Move To Dialog -->
    <MoveToDialog
      :key="showMoveToDialog"
      v-model="showMoveToDialog"
      :target="moveToDialogTarget"
      :listOfProjectsAndTasks="projectsList"
      :isShowCompletedChecked="filterClosedFoldersTasks"
      @closed="onCloseMoveToDialog"
      @refreshProjects="refreshProjects"
    />
    <!-- /Move To Dialog -->
  </div>
</template>
<script>
export default {
  name: 'ProjectsList',
  components: {
    ProjectsListSettingsDialog: () => import('@/components/global/ProjectsListSettingsDialog.vue'),
    CRUDTaskOrFolderDialog: () => import('@/components/manager_reports/CRUDTaskOrFolderDialog.vue'),
    AlertAboutDeleteDialog: () => import('@/components/manager_reports/AlertAboutDeleteDialog.vue'),
    ConfigDropdown: () => import('@/components/manager_reports/ConfigDropdown.vue'),
    RangeDatePickerMenu: () => import('@/components/global/RangeDatePickerMenu.vue'),
    MoveToDialog: () => import('@/components/manager_reports/MoveToDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Object,
      default: () => {
        return {
          selectedProjects: [],
          selectedTasks: [],
          selectedFolders: []
        }
      }
    },
    updateProjectsList: {
      required: false
    },
    redirectedProject: {
      required: false
    },
    redirectedClient: {
      required: false
    }
  },
  data () {
    return {
      search: '',
      showMoveToDialog: false,
      moveToDialogTarget: null,
      projectsList: [],
      filteredProjects: [],
      filterClosedFoldersTasks: false,
      projectsTree: [],
      showSettingsList: false,
      loading: false,
      selects: [],
      openedFolders: [],
      chooseDateRangeToImportFromJira: false,
      importFromJiraDateRange: [new Date().toISOString().substring(0, 10), new Date().toISOString().substring(0, 10)],
      waitForNotification: false,
      jiraApiTokenDialog: false,
      jiraApiDefaultLogin: null,
      jiraApiToken: '',
      projectIdToUpdate: null,
      fetchingTasks: false,
      selectAllProjects: false,
      showNotIncludedProjectDialog: false,
      showNotIncludedProjectsByClientDialog: false,
      redirectedProjects: null,
      notIncludedProjects: []
    }
  },
  computed: {
    updateShowToDialog () {
      if (this.showMoveToDialog) {
        return this.showMoveToDialog + '_' + Date.now()
      }
      return ''
    },
    indeterminate () {
      return this.value.selectedProjects && this.value.selectedProjects.length > 0 && this.value.selectedProjects.length !== this.filteredProjects.length
    },
    user () {
      return this.$store.getters.getUser
    },
    blockedProject () {
      return this.$store.getters.getBlockedProject
    },
    showRedirectedDialog: {
      get () {
        return this.showNotIncludedProjectDialog
      },
      set (value) {
        if (!value) {
          this.redirectingFromProjects()
        }
        this.showNotIncludedProjectDialog = value
      }
    },
    showRedirectedClientDialog: {
      get () {
        return this.showNotIncludedProjectsByClientDialog
      },
      set (value) {
        this.showNotIncludedProjectsByClientDialog = value
      }
    },
    showCRUDTaskOrFolderDialog () {
      return this.$store.getters.showEditTaskOrFolderDialog
    }
  },
  async mounted () {
    await this.getProjectsListWithTasks()
    this.filteredProjects = [...this.projectsTree]
    if (this.redirectedProject) {
      const selectedProject = this.filteredProjects.filter(item => item.Project_ID === this.redirectedProject)
      if (selectedProject.length) {
        this.selects = selectedProject
        this.$emit('input', {
          selectedProjects: [this.redirectedProject],
          selectedTasks: [],
          selectedFolders: []
        })
        this.getRedirectedReports()
      } else {
        this.showRedirectedDialog = true
      }
    } else if (this.redirectedClient) {
      this.getProjectsByClient()
    } else {
      this.selects = []
      this.$emit('input', {
        selectedProjects: [],
        selectedTasks: [],
        selectedFolders: []
      })
    }
  },
  methods: {
    onCloseMoveToDialog () {
      this.moveToDialogTarget = null
    },
    moveTo (e) {
      this.showMoveToDialog = true
      this.moveToDialogTarget = e
    },
    updateFilteredProjectsList () {
      setTimeout(() => {
        const excludedItemsArr = []
        this.$refs.projectlistTree.excludedItems.forEach(item => excludedItemsArr.push(item))
        this.filteredProjects = [...this.projectsList.filter(p => excludedItemsArr.indexOf(p.Item_ID) === -1)]
      })
    },
    updateProjectsTree (e) {
      const copyOfProjects = JSON.parse(JSON.stringify(this.projectsList))
      if (!e) {
        this.projectsTree = copyOfProjects.map(project => {
          project.Tasks = this.excludeTasksFolders(project.Tasks)
          return project
        })
      } else {
        this.projectsTree = copyOfProjects
      }
    },
    excludeTasksFolders (project) {
      return project.filter(task => {
        if (task.Status !== 'Completed') {
          task.Tasks = this.excludeTasksFolders(task.Tasks)
          return true
        } else {
          return false
        }
      })
    },
    addReporting (item) {
      item.Project_Name = this.projectsList.find(project => project.Project_ID === item.Project_ID).Name
      this.$emit('addReporting', item)
    },
    async onLabelClick (item, isOpen) {
      const onSelectsIndex = this.selects.findIndex(s => s.Item_ID === item.Item_ID)
      if (onSelectsIndex > -1) {
        this.selects.splice(onSelectsIndex, 1)
      }
      if (isOpen) {
        const itemToDeleteIndex = this.openedFolders.findIndex(i => i.Item_ID === item.Item_ID)
        this.openedFolders.splice(itemToDeleteIndex, 1)
      } else {
        await this.fetchTasksAndFolders(item)
        this.openedFolders.push(item)
      }
      if (onSelectsIndex > -1) {
        this.selects.push(item)
      }
    },
    onSelectAllChanged (e) {
      this.selectAllProjects = e

      if (e) {
        this.filteredProjects = this.projectsTree
        this.selects = this.filteredProjects
      } else {
        this.filteredProjects = []
        this.selects = []
      }
      this.$emit('input', {
        selectedProjects: this.selects.length ? this.selects.map(p => p.Project_ID) : [],
        selectedTasks: [],
        selectedFolders: []
      })
    },
    importReportsFromJira () {
      this.chooseDateRangeToImportFromJira = false
      this.axios.post('/api/integrations/import-jira-reports', {
        Project_ID: this.projectIdToImportReportsFromJira,
        Employee_ID: this.user.employee_id,
        Date_From: this.importFromJiraDateRange[0],
        Date_To: this.importFromJiraDateRange[1],
        Send_Notif_For: this.user.employee_id
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        if (res.data === 'no_api_token') {
          this.jiraApiDefaultLogin = this.user.e_mail
          this.jiraApiTokenDialog = true
        } else {
          this.$store.dispatch('blockProject', { projectID: this.projectIdToImportReportsFromJira })
        }
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    onImportFromJiraClick (e) {
      this.projectIdToImportReportsFromJira = e.Project_ID
      this.chooseDateRangeToImportFromJira = true
    },
    addToOpened (item) {
      this.openedFolders.push(item)
    },
    onSelect () {
      this.$emit('input', {
        selectedProjects: this.selects.filter(t => t.Type === 'Project').map(i => i.Project_ID),
        selectedTasks: this.selects.filter(t => t.Type === 'Task').map(i => i.Item_ID),
        selectedFolders: this.selects.filter(t => t.Type === 'Folder').map(i => i.Item_ID)
      })
    },
    async refreshProjects (e) {
      for (let i = 0; i < e.length; i++) {
        await this.updateProjectTasksList(e[i])
      }
    },
    async updateProjectTasksList (pId) {
      await this.axios.post('/api/tasks-and-folders-list', { Project_ID: pId }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        const ind = this.projectsList.findIndex(p => p.Project_ID === pId)
        this.projectsList[ind].Tasks = [...res.data]
        const inSelectsIndex = this.selects.findIndex(s => s.Project_ID === pId)
        const isOpenedIndex = this.openedFolders.findIndex(i => i.Item_ID === 'Project_' + pId)
        if (isOpenedIndex === -1 && inSelectsIndex > -1) {
          const indexToDelete = this.openedFolders.push(this.projectsList[ind])
          this.openFolders(this.projectsList[ind].Tasks)
          this.selects.splice(inSelectsIndex, 1)
          setTimeout(() => {
            this.selects.push(this.projectsList[ind])
            this.openedFolders.splice(indexToDelete - 1, 1)
          })
        }
        this.$emit('projectsListUpdated')
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    openFolders (items) {
      for (let i = 0; i < items.length; i++) {
        this.openedFolders.push(items[i])
        if (items[i].Tasks && items[i].Tasks.length) {
          this.openFolders(items[i].Tasks)
        }
      }
    },
    async getProjectsListWithTasks (newProjects) {
      try {
        this.loading = true
        const res = await this.axios.get('/api/selected-projects-list', {
          headers: { Authorization: `Bearer ${this.user.token}` }
        })

        this.projectsList = res.data

        if (!newProjects) return

        if (Array.isArray(newProjects)) {
          const projectIds = this.redirectedProjects?.map(p => p.Project_ID) || []
          this.selects = this.projectsList.filter(p => projectIds.includes(p.Project_ID))

          this.$emit('input', {
            selectedProjects: projectIds,
            selectedTasks: [],
            selectedFolders: []
          })

          this.getRedirectedReports()
          this.showRedirectedClientDialog = false
        } else {
          const selected = this.projectsList.find(p => p.Project_ID === newProjects.Project_ID)
          if (selected) {
            selected.Is_Selected = true
            this.selects = [selected]
          }
          this.showRedirectedDialog = false
        }
      } catch (error) {
        this.$store.dispatch('setError', {
          errorMsg: `${error.message} - ${error.response?.data || ''}`
        })
      } finally {
        this.loading = false
      }
    },
    saveJiraRestApiAccessInformation () {
      this.jiraApiTokenDialog = false
      this.axios.post('/api/integrations/jira-access', {
        Employee_ID: this.user.employee_id,
        Jira_Login: this.jiraApiDefaultLogin,
        API_Token: this.jiraApiToken,
        Project_ID: this.selectedProject
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.jiraApiTokenDialog = false
        this.chooseDateFromDialog = true
        this.$store.dispatch('setSuccess', { successMsg: 'Your access information successfuly saved' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    async fetchTasksAndFolders (e) {
      const projectOfList = this.projectsList.find(project => project.Item_ID === e.Item_ID)
      if (e.Type === 'Project' && !projectOfList.Tasks.length) {
        this.fetchingTasks = true
        await this.axios.post('/api/tasks-and-folders-list', { Project_ID: e.Project_ID }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          projectOfList.Tasks.push(...res.data)
          this.fetchingTasks = false
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        })
      }
    },
    addRedirectedProjectToList () {
      this.axios.post('/api/projects-list-cmp/redirectedproject', { Project_ID: this.redirectedProject }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getProjectsListWithTasks(res.data[0])
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    redirectingFromProjects () {
      const selectedProject = this.filteredProjects.filter(item => item.Project_ID === this.redirectedProject)
      if (selectedProject.length) {
        this.selects = selectedProject
      }
      this.$emit('input', {
        selectedProjects: [this.redirectedProject],
        selectedTasks: [],
        selectedFolders: []
      })
    },
    addRedirectedProjectsByClientToList () {
      this.axios.post('/api/projects-list-cmp/redirectedprojects', { Projects: this.notIncludedProjects.map(obj => obj.Project_ID).join(', ') }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getProjectsListWithTasks(this.redirectedProjects)
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    getProjectsByClient () {
      this.axios.get(`/api/projects-list-cmp/client/${this.redirectedClient}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.redirectedProjects = res.data
        const projectIdsOld = this.filteredProjects.map(obj => obj.Project_ID)
        const projectIdsNew = this.redirectedProjects.map(obj => obj.Project_ID)
        const checkedProjects = projectIdsNew.every(projectId => projectIdsOld.includes(projectId))
        if (checkedProjects) {
          this.selects = this.projectsList.filter(obj => projectIdsNew.includes(obj.Project_ID))
          this.$emit('input', {
            selectedProjects: projectIdsNew,
            selectedTasks: [],
            selectedFolders: []
          })
          this.getRedirectedReports()
        } else {
          this.notIncludedProjects = this.redirectedProjects.filter(project => !this.filteredProjects.some(redirectedProject => redirectedProject.Project_ID === project.Project_ID))
          this.showRedirectedClientDialog = true
        }
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    selectRedirectedProjectsWithoutAdding () {
      const newProjects = this.redirectedProjects.map(obj => obj.Project_ID)
      this.selects = this.projectsList.filter(obj => this.redirectedProjects.some(newProject => newProject.Project_ID === obj.Project_ID))
      this.$emit('input', {
        selectedProjects: newProjects,
        selectedTasks: [],
        selectedFolders: []
      })
      this.getRedirectedReports()
      this.showRedirectedClientDialog = false
    },
    getRedirectedReports () {
      setTimeout(() => {
        this.$emit('getReportsOnRedirect')
      })
    }
  },
  watch: {
    blockedProject (newVal) {
      if (!newVal) {
        this.updateProjectTasksList(this.projectIdToImportReportsFromJira)
      }
    },
    updateProjectsList (newVal) {
      if (newVal !== null) {
        this.updateProjectTasksList(newVal)
      }
    },
    projectsList: {
      handler () {
        this.updateProjectsTree(this.filterClosedFoldersTasks)
      },
      deep: true
    },
    filterClosedFoldersTasks (newValue) {
      this.updateProjectsTree(newValue)
      this.$emit('changeFilterClosedFoldersTasks', newValue)
    }
  }
}
</script>
<style lang="scss" scoped>
  ::v-deep .v-treeview-node__toggle {
    display: none;
  }

  ::v-deep .v-treeview-node__level {
    width: 15px;
  }

  ::v-deep .v-treeview-node__root {
    position: relative;
    background-color: inherit;
    z-index: auto;
  }

  ::v-deep .v-treeview-node--leaf .v-treeview-node__root .v-treeview-node__level:first-child {
    display: none;
  }

  .project-list-container {
    height: calc(45vh - 15px);
  }

  .card-text-container {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .loading-container {
    width: 100%;
    height: 30px;
    background-color: rgba(#d7d7d7, .5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
  }

  .sticky-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: inherit;
    flex-shrink: 0;
  }

  .projects-list {
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .projects-title {
    display: flex;
    justify-content: space-between;
    align-items: start;
  }

  .projects-title-actions {
    display: flex;
    flex-wrap: nowrap;
  }

  .normal-worlds-wrapping {
    word-break: normal;
  }
</style>
